import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { SelectInput } from "components/Select";
import { formatOptionLabel } from "@/utils/format-option-label";
import { getSelectClasses } from "@/utils/filter-styles";

import {
  ACTIVE_STATUS_OPTIONS,
  SECTOR_LABELS,
  SECTORS,
  SHIPPING_TYPES,
  SectorEnum,
  ShippingTypeEnum,
} from "../constants";
import { useSectorEmailsFilterActions } from "../filter/hooks/actions";
import { AppliedSectorEmailsFilters } from "../filter/types";
import { SECTOR_EMAILS_LABELS } from "../messages";

interface SelectOption {
  value: string;
  label: string;
}

interface FilterSelectsProps {
  applied: AppliedSectorEmailsFilters;
}

const SELECT_CLASS_NAME: string = "h-9 min-w-[200px]";

const SELECT_CLASS_PREFIX: string = "an-simple-select";

const ACTIVE_TRUE: string = "true";

const SECTOR_OPTIONS: readonly SelectOption[] = SECTORS.map(
  (sector: SectorEnum): SelectOption => ({
    value: sector,
    label: SECTOR_LABELS[sector],
  })
);

const SHIPPING_TYPE_OPTIONS: readonly SelectOption[] = SHIPPING_TYPES.map(
  (type): SelectOption => ({ value: type.value, label: type.label })
);

const ACTIVE_OPTIONS: readonly SelectOption[] = ACTIVE_STATUS_OPTIONS.map(
  (option): SelectOption => ({
    value: String(option.value),
    label: option.label,
  })
);

const optionsFor = (
  options: readonly SelectOption[],
  selected: readonly string[]
): SelectOption[] =>
  options.filter((option: SelectOption): boolean =>
    selected.includes(option.value)
  );

const valuesOf = (selected: readonly SelectOption[] | null): string[] =>
  (selected ?? []).map((option: SelectOption): string => option.value);

export function FilterSelects({ applied }: FilterSelectsProps): JSX.Element {
  const { applyFilters } = useSectorEmailsFilterActions();

  const applyWith = (patch: Partial<AppliedSectorEmailsFilters>): void => {
    applyFilters({ ...applied, ...patch });
  };

  return (
    <>
      <SelectInput
        options={SECTOR_OPTIONS}
        placeholder={SECTOR_EMAILS_LABELS.FILTER_SECTOR}
        aria-label={SECTOR_EMAILS_LABELS.FILTER_SECTOR}
        value={optionsFor(SECTOR_OPTIONS, applied.sectors)}
        onChange={(selected: readonly SelectOption[] | null): void =>
          applyWith({ sectors: valuesOf(selected) as SectorEnum[] })
        }
        className={`${getSelectClasses(applied.sectors.length > 0)} ${SELECT_CLASS_NAME}`}
        classNamePrefix={SELECT_CLASS_PREFIX}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />

      <SelectInput
        options={SHIPPING_TYPE_OPTIONS}
        placeholder={SECTOR_EMAILS_LABELS.FILTER_SHIPPING_TYPE}
        aria-label={SECTOR_EMAILS_LABELS.FILTER_SHIPPING_TYPE}
        value={optionsFor(SHIPPING_TYPE_OPTIONS, applied.shipping_types)}
        onChange={(selected: readonly SelectOption[] | null): void =>
          applyWith({
            shipping_types: valuesOf(selected) as ShippingTypeEnum[],
          })
        }
        className={`${getSelectClasses(applied.shipping_types.length > 0)} ${SELECT_CLASS_NAME}`}
        classNamePrefix={SELECT_CLASS_PREFIX}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />

      <SelectInput
        options={ACTIVE_OPTIONS}
        placeholder={SECTOR_EMAILS_LABELS.FILTER_STATUS}
        aria-label={SECTOR_EMAILS_LABELS.FILTER_STATUS}
        value={optionsFor(
          ACTIVE_OPTIONS,
          applied.active.map((value: boolean): string => String(value))
        )}
        onChange={(selected: readonly SelectOption[] | null): void =>
          applyWith({
            active: valuesOf(selected).map(
              (value: string): boolean => value === ACTIVE_TRUE
            ),
          })
        }
        className={`${getSelectClasses(applied.active.length > 0)} ${SELECT_CLASS_NAME}`}
        classNamePrefix={SELECT_CLASS_PREFIX}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />
    </>
  );
}
